import { useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import MuiAutocomplete from "@mui/material/Autocomplete";
import { CircularProgress } from "@mui/material";
import debounce from "lodash.debounce";

export interface AutocompleteProps<T> {
  helperText?: string;
  noOptionsText?: string;
  optionLabelKey: keyof T;
  label: string;
  fetchData: (name: string) => Promise<T[]>;
  onChange: (event: T | null) => void;
}

export default function AsyncAutocomplete<T extends Record<string, any>>(props: AutocompleteProps<T>): JSX.Element {
  const { helperText, label, noOptionsText, optionLabelKey, fetchData, onChange } = props;
  const [value, setValue] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<T[]>([]);

  const fetch = useMemo(
    () =>
      debounce(async (name: string, callback: (results: T[]) => void) => {
        setLoading(true);

        const res = await fetchData(name);
        callback(res);

        setLoading(false);
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;

    fetch(inputValue, (results) => {
      if (active) {
        setOptions(results);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <MuiAutocomplete
      autoComplete
      filterOptions={(x) => x}
      filterSelectedOptions
      getOptionLabel={(option) => option[optionLabelKey]}
      includeInputInList
      isOptionEqualToValue={(option, v) => option.id === v.id}
      noOptionsText={noOptionsText}
      options={options}
      value={value}
      onChange={(event, newValue: T | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color='primary' size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
